import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const IN_EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, className = "", delay = 0 }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  const dist = 14;
  const variants = {
    out: { opacity: 0, y: dist },
    in: { opacity: 1, y: 0, transition: { duration: 0.45, ease: IN_EASE, delay: Math.min(delay, 0.12) } },
  };
  return (
    <motion.div
      className={className}
      variants={variants}
      initial="out"
      whileInView="in"
      viewport={{ once: true, amount: 0.05 }}
    >
      {children}
    </motion.div>
  );
};

export const Img = ({ name, alt, w, h, sizes = "(min-width: 1024px) 50vw, 100vw", priority = false, className = "", style }) => (
  <img
    style={style}
    src={`/img/${name}-1200.webp`}
    srcSet={`/img/${name}-640.webp 640w, /img/${name}-1200.webp 1200w`}
    sizes={sizes}
    alt={alt}
    width={w}
    height={h}
    loading={priority ? "eager" : "lazy"}
    fetchPriority={priority ? "high" : undefined}
    decoding="async"
    className={className}
  />
);

// Static frame: the anchored site background provides the depth effect.
export const Parallax = ({ children, className = "" }) => (
  <div className={`${className.includes("absolute") ? "" : "relative"} overflow-hidden ${className}`}>
    <div className="absolute inset-0">{children}</div>
  </div>
);

const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);

export const Counter = ({ value, decimals = 0, suffix = "" }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(reduce ? value : 0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (reduce || typeof IntersectionObserver === "undefined") {
      setDisplay(value);
      return undefined;
    }
    let raf = 0;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - start) / 1500);
        setDisplay(p === 1 ? value : value * ease(p));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.3 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [value, reduce]);
  const final = `${value.toFixed(decimals)}${suffix}`;
  return (
    <span ref={ref} className="tabular-nums" data-final={final}>
      <span aria-hidden="true">{display.toFixed(decimals)}{suffix}</span>
      <span className="sr-only">{final}</span>
    </span>
  );
};
